import { useState } from 'react';
import { wouldTimeCauseOverlap, isValidShiftDuration } from '../../shiftUtils';
import { SHIFT_TYPES } from '../../constants/shiftConstants';
import { useSelector } from 'react-redux';
import { selectCurrentVenue } from '../../../redux/slices/currentUserSlice';
import { getVenueTimezone } from '../../venueTimeFormatters';
import dayjs from '../../dayjsConfig';

// --- Extracted Helper Functions ---
const getFallbackTime = (baseTime, offset) => {
  const fallbackTime = new Date(baseTime);
  fallbackTime.setHours(fallbackTime.getHours() + offset);
  return fallbackTime;
};

const isTimeInPast = (time) => {
  const now = new Date();
  return time < now;
};
const isStartTimeAfterEndTime = (start, end) => start > end;
const isEndTimeBeforeStartTime = (end, start) => end < start;
const wouldCauseOverlap = (time, isStart, shift, shiftsByAssignment) =>
  wouldTimeCauseOverlap(time, isStart, shift, shiftsByAssignment);

const getAdjustedEndTimeForStartOnNewDay = (newStart, oldStart, oldEnd, venueTimezone) => {
  // Calculate the duration between old start and old end
  const oldStartInTz = dayjs(oldStart).tz(venueTimezone);
  const oldEndInTz = dayjs(oldEnd).tz(venueTimezone);
  const duration = oldEndInTz.diff(oldStartInTz);

  // Add the duration to the new start time to preserve the shift duration
  const newEndTime = dayjs(newStart).tz(venueTimezone).add(duration, 'millisecond').toDate();
  return newEndTime;
};

const getPotentialShift = (shift, updates) => ({ ...shift, ...updates });

export const useShiftTimeValidation = (shift, shiftsByAssignment, onShiftUpdate) => {
  const [shiftErrorMessage, setShiftErrorMessage] = useState({});
  const currentVenue = useSelector(selectCurrentVenue);
  const venueTimezone = getVenueTimezone(currentVenue);

  // Helper function to check and set error messages
  const setShiftError = (message, type) => {
    setShiftErrorMessage({ message, type });
    return false;
  };

  const handleStartTimeChange = (newStartTime) => {
    // Check for 5 cases:
    // 1. Start time is on a different date
    // 2. Start time is in the past
    // 3. Start time is after end time
    // 4. Shift duration is less than the minimum
    // 5. New start time would cause overlap

    setShiftErrorMessage({}); // Reset error message
    let fallbackEndTime = getFallbackTime(newStartTime, 1);

    // CASE 1: Start time is on a different date (move end time to preserve shift duration)
    if (shift?.start && shift?.end) {
      const oldStart = new Date(shift.start);
      const oldEnd = new Date(shift.end);

      const oldStartInVenueTz = dayjs(oldStart).tz(venueTimezone);
      const newStartInVenueTz = dayjs(newStartTime).tz(venueTimezone);
      const startMovedToNewDay = !newStartInVenueTz.isSame(oldStartInVenueTz, 'day');

      if (startMovedToNewDay) {
        // Move end time to new date, preserve shift duration
        const newEndTime = getAdjustedEndTimeForStartOnNewDay(newStartTime, oldStart, oldEnd, venueTimezone);

        // Check for overlap and minimum duration
        const potentialShift = getPotentialShift(shift, { start: newStartTime, end: newEndTime });
        // CASE 1a: Start time is in the past
        if (isTimeInPast(newStartTime)) {
          return setShiftError('Start time cannot be in the past.', 'start');
        }
        // CASE 1b: Shift duration is less than the minimum
        if (!isValidShiftDuration(newStartTime, newEndTime)) {
          return setShiftError('Shift duration is less than the minimum allowed.', SHIFT_TYPES.START);
        }
        // CASE 1c: New start or end time would cause overlap
        if (
          wouldCauseOverlap(newStartTime, true, potentialShift, shiftsByAssignment) ||
          wouldCauseOverlap(newEndTime, false, potentialShift, shiftsByAssignment)
        ) {
          return setShiftError(`Shift not updated. This would double-book ${shift?.assignedName}`, SHIFT_TYPES.START);
        }
        return onShiftUpdate({ start: newStartTime, end: newEndTime });
      }
    }

    // CASE 2: Start time is in the past
    if (isTimeInPast(newStartTime)) {
      return setShiftError('Start time cannot be in the past.', SHIFT_TYPES.START);
    }

    // CASE 3: Start time is after end time
    if (isStartTimeAfterEndTime(newStartTime, new Date(shift?.end))) {
      const potentialShift = getPotentialShift(shift, { start: newStartTime });
      fallbackEndTime = getFallbackTime(newStartTime, 1);
      // Check for overlap
      if (wouldCauseOverlap(fallbackEndTime, false, potentialShift, shiftsByAssignment)) {
        return setShiftError(`Shift not updated. This would double-book ${shift?.assignedName}`, SHIFT_TYPES.START);
      }
      return onShiftUpdate({ start: newStartTime, end: fallbackEndTime });
    }

    // CASE 4: Shift duration is less than the minimum
    if (!isValidShiftDuration(newStartTime, shift?.end)) {
      const potentialShift = getPotentialShift(shift, { start: newStartTime });
      fallbackEndTime = getFallbackTime(newStartTime, 1);
      // Check for overlap
      if (wouldCauseOverlap(fallbackEndTime, false, potentialShift, shiftsByAssignment)) {
        return setShiftError(`Shift not updated. This would double-book ${shift?.assignedName}`, SHIFT_TYPES.START);
      }
      return onShiftUpdate({ start: newStartTime, end: fallbackEndTime });
    }

    // CASE 5: New start time would cause overlap
    if (wouldCauseOverlap(newStartTime, true, shift, shiftsByAssignment)) {
      const potentialShift = getPotentialShift(shift, { start: newStartTime });
      fallbackEndTime = getFallbackTime(newStartTime, 1);
      // Check for overlap
      if (wouldCauseOverlap(fallbackEndTime, false, potentialShift, shiftsByAssignment)) {
        return setShiftError(`Shift not updated. This would double-book ${shift?.assignedName}`, SHIFT_TYPES.START);
      }
      return onShiftUpdate({ start: newStartTime, end: fallbackEndTime });
    }

    // If all checks pass, update start time only
    onShiftUpdate({ start: newStartTime });
    return true;
  };

  const handleEndTimeChange = (newEndTime) => {
    // Check for 4 cases:
    // 1. End time is in the past
    // 2. End time is before start time
    // 3. Shift duration is less than the minimum
    // 4. New end time would cause overlap

    setShiftErrorMessage({}); // Reset error message
    let fallbackStartTime = getFallbackTime(newEndTime, -1);

    // CASE 1: End time is in the past
    if (isTimeInPast(newEndTime)) {
      return setShiftError('End time cannot be in the past.', SHIFT_TYPES.END);
    }

    // CASE 2: End time is before start time
    if (isEndTimeBeforeStartTime(newEndTime, new Date(shift?.start))) {
      const potentialShift = getPotentialShift(shift, { end: newEndTime });
      fallbackStartTime = getFallbackTime(newEndTime, -1);
      if (fallbackStartTime < new Date()) {
        return setShiftError('End time cannot be less than 1 hour from now.', SHIFT_TYPES.END);
      }
      // Check for overlap
      if (wouldCauseOverlap(fallbackStartTime, true, potentialShift, shiftsByAssignment)) {
        return setShiftError(`Shift not updated. This would double-book ${shift?.assignedName}`, SHIFT_TYPES.END);
      }
      return onShiftUpdate({ start: fallbackStartTime, end: newEndTime });
    }

    // CASE 3: Shift duration is less than the minimum
    if (!isValidShiftDuration(shift?.start, newEndTime)) {
      const potentialShift = getPotentialShift(shift, { end: newEndTime });
      fallbackStartTime = getFallbackTime(newEndTime, -1);
      if (fallbackStartTime < new Date()) {
        return setShiftError('End time cannot be less than 1 hour from now.', SHIFT_TYPES.END);
      }
      // Check for overlap
      if (wouldCauseOverlap(fallbackStartTime, true, potentialShift, shiftsByAssignment)) {
        return setShiftError(`Shift not updated. This would double-book ${shift?.assignedName}`, SHIFT_TYPES.END);
      }
      return onShiftUpdate({ start: fallbackStartTime, end: newEndTime });
    }

    // CASE 4: New end time would cause overlap
    if (wouldCauseOverlap(newEndTime, false, shift, shiftsByAssignment)) {
      const potentialShift = getPotentialShift(shift, { end: newEndTime });
      fallbackStartTime = getFallbackTime(newEndTime, -1);
      // Check for overlap
      if (wouldCauseOverlap(fallbackStartTime, true, potentialShift, shiftsByAssignment)) {
        return setShiftError(`Shift not updated. This would double-book ${shift?.assignedName}`, SHIFT_TYPES.END);
      }
      return onShiftUpdate({ start: fallbackStartTime, end: newEndTime });
    }

    // If all checks pass, update end time only
    onShiftUpdate({ end: newEndTime });
    return true;
  };

  return {
    handleStartTimeChange,
    handleEndTimeChange,
    shiftErrorMessage,
    setShiftErrorMessage,
  };
};
